import { Component, ElementRef } from '@angular/core';
import { Params } from '@angular/router';

import { UtilityFunctions } from '../../../classes/UtilityFunctions';
import { DataSetFilter, FilterSpec } from '../../../classes/DataSetFilter';
import { DataSet, DataRequestReply, DataRequestError } from '../../../interfaces/DisplayInterfaces';
import { BaseWidget } from '../BaseWidget/BaseWidget.component';
import { DataConsumerConfiguration, ManagedDataReply } from '../../../interfaces/WidgetInterfaces';

import { DisplayStateService } from '../../../services/DisplayStateService';
import { AppStateService } from '../../../services/AppStateService';
import { WebPlatformApiService } from '../../../services/WebPlatformApiService';
import { ClientSettingsService } from '../../../services/ClientSettingsService';

// This is the required order of operations:

// RequestData()      -- starts process to obtain data for originalDataSet
// HandleGrouping()   -- groups originalDataSet to displayDataSet if grouping rules exist, otherwise displayDataSet stays undefined
// HandleFilters()    -- filters displayDataSet if defined or originalDataSet if filters exist. displayDataSet will always be defined at end
// HandleSorts()      -- sorts displayDataSet if there are any sorts
// HandleMessages()   -- applies any manipulation based on messages after all the other operations have completed.
// The operations can be started anywhere in the list, but all following operations must be performed.

@Component({
  selector: 'app-base-manaaged-data-widget-component',
  template: `<p>
  This is an 'abstract' widget and should not be displayed.
</p>
`,
  styles: [``]
})
// tslint:disable-next-line:component-class-suffix
export class BaseManagedDataWidget extends BaseWidget {

  originalDataSet: DataSet;
  displayDataSet: DataSet;

  dataRequestError: DataRequestError;
  context: Params = [];
  messages: Params = [];
  filters: Params = [];
  sorts: Params = [];

  filterlkp: Params = [];

  dataKey: string = undefined;

  constructor(
    protected element: ElementRef,
    protected clientSettingsService: ClientSettingsService,
    protected displayStateService: DisplayStateService,
    protected appStateService: AppStateService,
    protected webPlatformApiService: WebPlatformApiService) {
      super(element, clientSettingsService, displayStateService, appStateService, webPlatformApiService);
    }
  // Overrides from BaseWidget
  onInitialize()                            { this.context = this.currentContext();   this.RequestData(); }
  onNewMessage(messageParams: Params)       { this.messages = messageParams;          this.HandleMessages();  }
  onContextChanged(currentContext: Params)  { this.context = currentContext;          this.RequestData();         }
  onFiltersChanged(currentFilters: Params)  { this.filters = currentFilters;          this.HandleFilters();   }
  onSortsChanged(currentSorts: Params)      { this.sorts = currentSorts;              this.HandleSorts();     }

  private currentContext(): Params {
    return this.displayStateService.appState.current.context;
  }

  RequestDataByDataConsumerConfig(widgetName: string, dcc: DataConsumerConfiguration) {
    this.displayStateService.Log(5, `BaseManagedDataWidget->${this.Config.name} - -> displayStateService.RequestData`);
    const reply = this.displayStateService.RequestData(this.Config.name, dcc);
    if (reply && reply.RequestKey) {
      this.dataKey = reply.RequestKey;
    }
    if (reply && reply.Available === true) {
      this.displayStateService.Log(4, `BaseManagedDataWidget->${this.Config.name}.RequestData - request complete, data available`);
      // UtilityFunctions.DebugLog(`BaseManagedDataWidget: request complete, data available`);
      this.onDataAvailable(reply);
    } else {
      this.displayStateService.Log(4, `BaseManagedDataWidget->${this.Config.name}.RequestData - request complete, data PENDING`);
    }
  }

  RequestData() {
    this.displayStateService.Log(5, `BaseManagedDataWidget->${this.Config.name} - RequestData`);
    // Check that the current context still means this widget is in scope.  It can occur that a new context is selected by the
    // user and this widget has gone out of scope but hasn't been destroyed yet.
    const stillMatchesContext =
      UtilityFunctions.MatchWidgetConfiguration(this.context, this.Config, this.clientSettingsService, this.appStateService);

    this.displayDataSet = undefined;

    // Async request for managed data and stored in dataSet variable
    if (stillMatchesContext && this.Config && this.Config.dataConsumerConfiguration) {
      this.RequestDataByDataConsumerConfig(this.Config.name, this.Config.dataConsumerConfiguration);
    } else {
      this.originalDataSet = undefined;
    }
  }

  onDataAvailable(reply: DataRequestReply) {
    this.displayStateService.Log(5,
      `BaseManagedDataWidget->${this.Config.name} - onDataAvailable - waiting for  ${this.dataKey} and got ${reply.RequestKey}`);
    if (this.dataKey !== undefined && this.dataKey === reply.RequestKey && reply.Available === true) {
      this.originalDataSet = <DataSet>reply.ImmediateManagedDataReply.CacheData.Payload;
        this.onManagedDataReply(reply.ImmediateManagedDataReply);
        this.HandleGrouping();
    }
  }

  onDataError(error: DataRequestError) {
    this.displayStateService.Log(5,
      `BaseManagedDataWidget->${this.Config.name} - onDataError - waiting for  ${this.dataKey} and got ${error.RequestKey}`);
    if (this.dataKey !== undefined && this.dataKey === error.RequestKey) {
      this.dataRequestError = error;
      this.onManagedDataError(error);
    }
  }

  onManagedDataError(error: DataRequestError) {}

  onManagedDataReply(managedDataReply: ManagedDataReply) {}

  HandleGrouping() {
    this.displayStateService.Log(5, `BaseManagedDataWidget->${this.Config.name}.HandleGrouping`);
    if (this.originalDataSet !== undefined) {
      if (this.Config && this.Config.dataConsumerConfiguration && this.Config.dataConsumerConfiguration.dataGrouping !== undefined) {
        this.displayStateService.Log(4, `BaseManagedDataWidget->${this.Config.name}.HandleGrouping: applying`);
        this.originalDataSet = DataSetFilter.GroupDataSet(this.originalDataSet, this.Config.dataConsumerConfiguration.dataGrouping);
      }
      this.filterlkp = [];
      this.HandleFilters();
    }
  }

  HandleFilters() {
    this.displayStateService.Log(5, `BaseManagedDataWidget->${this.Config.name}.HandleFilters`);
    if (this.originalDataSet !== undefined) {

      this.filterlkp = [];
      Object.keys(this.displayStateService.appState.current.filters).forEach( k => {

        const userExp = FilterSpec.FormattedExpressionToUser({ name: k, value: this.displayStateService.appState.current.filters[k] });
        if (this.originalDataSet.columns.indexOf(userExp.name) > -1) {
          this.filterlkp[userExp.name] = userExp.value;
        }
      });

      if (this.Config && this.Config.dataConsumerConfiguration) {
        this.displayDataSet = DataSetFilter.FilterDataSet(
          this.originalDataSet,
          this.Config.dataConsumerConfiguration.filterConfiguration,
          this.context,
          this.displayStateService.appState.current.filters);  
      }

      this.HandleSorts();

    }
  }

  HandleSorts() {
    if (this.Config && this.Config.dataConsumerConfiguration) {
      this.displayStateService.Log(5, `BaseManagedDataWidget->${this.Config.name}.HandleSorts`);
      if (this.displayDataSet !== undefined) {
        DataSetFilter.SortDataSet(this.displayDataSet, this.sorts, this.Config.dataConsumerConfiguration.sortColumns);
        this.displayStateService.Log(5, `BaseManagedDataWidget->${this.Config.name} - calling PostProcessing()`);
      }
    }
    this.PostProcessing();
    this.HandleMessages();
  }

  PostProcessing() {
    // Blank - child components can override this to handle
  }

  HandleMessages() {
    // Blank - child components can override to handle
  }
}
